"use client"

/**
 * The chat's card for a deck that is still being GENERATED (defect #38,
 * 2026-10-19).
 *
 * A user confirmed "Generate a flash-card deck" for a 16-card OWASP Top 10
 * deck; the chat said the job had started and showed nothing to click, and
 * nothing came back when it ended. He could not find the deck. At confirm
 * time no deck exists yet — the tool answers with the generation SESSION, and
 * the worker drafts the deck row minutes later — so the card opens the AI
 * Studio's live build of that session (`entity.href`), and follows the run:
 *
 *   - it reads `GET /content-studio/ai/sessions/:id`, the same request and
 *     cache entry the AI Studio page reads (`contentStudioKeys.sessions
 *     .detail`), every DECK_CARD_POLL_MS while the run is In Progress;
 *   - polling ends when the run ends, when the message unmounts (TanStack
 *     drops the observer), when a read fails (then it claims nothing about
 *     the run), and after DECK_CARD_POLL_CAP_MS for a run that never ends (a
 *     stalled In Progress) — the link stays in every case;
 *   - an ended run says what happened and links the deck it made (save-first
 *     stamps `deckId` before the quality check, so Needs Review has one too).
 *
 * The prompt is the user's own text: shown through <UserText>, never parsed.
 */
import { useEffect, useState } from "react"
import Link from "next/link"
import { useQuery } from "@tanstack/react-query"
import { AlertTriangle, ArrowRight, CheckCircle2, Clock, Info, Layers, Loader2 } from "lucide-react"
import { UserText } from "@/components/ui/user-text"
import { csFetch } from "@/lib/content-studio/api"
import { useWorkspaceScopedEnabled } from "@/lib/api/use-workspace-scoped-enabled"
import {
  normalizeGenerationSessionSummary,
  type GenerationSession,
} from "@/lib/content-studio/session-types"
import { contentStudioKeys } from "@/components/content-studio/hooks/query-keys"
import { useTranslation } from "@/lib/i18n"
import type { EntityCard } from "@/lib/ai-chat/types"

/** How often a building run is re-read. A deck takes minutes; this is calm. */
export const DECK_CARD_POLL_MS = 5_000
/** After this the card stops asking, and says to follow it in Content Studio. */
export const DECK_CARD_POLL_CAP_MS = 30 * 60_000

type CardState = "building" | "stillBuilding" | "unknown" | "ready" | "needsReview" | "failed" | "cancelled"
type Translate = ReturnType<typeof useTranslation>["t"]

/** One literal t() per state, so the static-keys guard sees every key. */
function statusText(state: CardState, t: Translate): string {
  switch (state) {
    case "ready":
      return t("contentStudio.chatCard.ready")
    case "needsReview":
      return t("contentStudio.chatCard.needsReview")
    case "failed":
      return t("contentStudio.chatCard.failed")
    case "cancelled":
      return t("contentStudio.chatCard.cancelled")
    case "stillBuilding":
      return t("contentStudio.chatCard.stillBuilding")
    case "unknown":
      return t("contentStudio.chatCard.unknown")
    case "building":
      return t("contentStudio.chatCard.building")
  }
}

export function DeckGenerationCard({ entity }: { entity: EntityCard }) {
  const { t } = useTranslation()
  const workspaceReady = useWorkspaceScopedEnabled()
  const [capped, setCapped] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setCapped(true), DECK_CARD_POLL_CAP_MS)
    return () => clearTimeout(timer)
  }, [])

  const query = useQuery({
    queryKey: contentStudioKeys.sessions.detail(entity.id),
    queryFn: () => csFetch<GenerationSession>(`ai/sessions/${encodeURIComponent(entity.id)}`),
    select: normalizeGenerationSessionSummary,
    enabled: workspaceReady && !capped,
    refetchInterval: (query) => {
      if (capped || query.state.status === "error") return false
      const raw = query.state.data
      return !raw || raw.status === "In Progress" ? DECK_CARD_POLL_MS : false
    },
  })
  const session = query.data

  const state: CardState =
    session?.status === "Completed"
      ? "ready"
      : session?.status === "Needs Review"
        ? "needsReview"
        : session?.status === "Failed"
          ? "failed"
          : session?.status === "Cancelled"
            ? "cancelled"
            : query.isError && !session
              ? "unknown"
              : capped
                ? "stillBuilding"
                : "building"

  const deckHref =
    session?.deckId && (state === "ready" || state === "needsReview")
      ? `/content-studio/decks/${encodeURIComponent(session.deckId)}`
      : null
  const action = deckHref
    ? {
        href: deckHref,
        label:
          state === "needsReview" ? t("contentStudio.chatCard.reviewDeck") : t("contentStudio.chatCard.openDeck"),
      }
    : { href: entity.href, label: t("contentStudio.chatCard.openStudio") }

  const StatusIcon =
    state === "ready"
      ? CheckCircle2
      : state === "building"
        ? Loader2
        : state === "stillBuilding"
          ? Clock
          : state === "unknown"
            ? Info
            : AlertTriangle

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-border bg-muted/30 text-sm">
      <div className="grid gap-1.5 px-3 py-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          <Layers className="h-3 w-3 shrink-0" aria-hidden="true" />
          {t("contentStudio.chatCard.kind")}
        </div>

        <UserText className="line-clamp-2 break-words font-medium text-foreground">{entity.name}</UserText>

        <p className="flex items-start gap-1.5 text-xs text-muted-foreground" role="status" aria-live="polite">
          <StatusIcon
            className={
              state === "building"
                ? "mt-0.5 h-3 w-3 shrink-0 animate-spin"
                : state === "ready"
                  ? "mt-0.5 h-3 w-3 shrink-0 text-emerald-600"
                  : state === "stillBuilding" || state === "unknown"
                    ? "mt-0.5 h-3 w-3 shrink-0"
                    : "mt-0.5 h-3 w-3 shrink-0 text-amber-600"
            }
            aria-hidden="true"
          />
          <span>{statusText(state, t)}</span>
        </p>
      </div>

      <Link
        href={action.href}
        className="flex w-full items-center gap-1.5 border-t border-border/60 px-3 py-1.5 text-xs font-medium text-primary hover:bg-muted/60 hover:underline"
      >
        {action.label}
        <ArrowRight className="rtl-flip h-3 w-3 shrink-0" aria-hidden="true" />
      </Link>
    </div>
  )
}
